import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { queryClient } from '@/api/query-client';
import { clearFaroUser } from '@/lib/observability/faro-client';
import { useRestaurantStore } from './restaurant.store';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  email: string | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  login: (
    tokens: { accessToken: string; refreshToken: string; expiresIn: number },
    email: string,
  ) => void;
  setTokens: (accessToken: string, expiresIn: number, refreshToken?: string) => void;
  logout: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  email: null,
  isAuthenticated: false,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      login: (tokens, email) => {
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: Date.now() + tokens.expiresIn * 1000,
          email,
          isAuthenticated: true,
        });
      },

      setTokens: (accessToken, expiresIn, refreshToken) => {
        set((state) => ({
          accessToken,
          expiresAt: Date.now() + expiresIn * 1000,
          refreshToken: refreshToken ?? state.refreshToken,
        }));
      },

      logout: () => {
        set({ ...initialState });
        // Drop the restaurant selection + cached context too, so the next user
        // to log in on this browser starts clean instead of inheriting the
        // previous account's selected restaurant (which would 403).
        useRestaurantStore.getState().clear();
        // The react-query cache outlives the store reset, and every entry in it
        // belongs to the account that just left - most importantly ['auth','me'],
        // which the restaurant picker reads with a 10-minute staleTime. Keeping
        // it means the next account logs in and is auto-sent to the PREVIOUS
        // account's restaurant, which the API then 403s.
        queryClient.clear();
        // Faro keeps the user meta for the whole page lifetime, so without this the
        // next account's errors are filed under the account that just left.
        clearFaroUser();
      },
    }),
    {
      name: 'restaurant-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        email: state.email,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
